import { useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import FormLabel from '@mui/material/FormLabel';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import DOMPurify from 'dompurify';

type RichTextEditorProps = Readonly<{
  disabled?: boolean;
  label?: string;
  minHeight?: number;
  onChange: (value: string) => void;
  value: string;
}>;

type RichTextContentProps = Readonly<{
  html: string;
  minHeight?: number;
}>;

type ToolbarButtonProps = Readonly<{
  active?: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
  title: string;
}>;

const RICH_TEXT_ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'em',
  'u',
  's',
  'h2',
  'h3',
  'ul',
  'ol',
  'li',
] as const;

const RICH_TEXT_ALLOWED_ATTR = [] as const;

export function sanitizeRichTextHtml(html: string) {
  return DOMPurify.sanitize(html || '', {
    ALLOWED_ATTR: [...RICH_TEXT_ALLOWED_ATTR],
    ALLOWED_TAGS: [...RICH_TEXT_ALLOWED_TAGS],
  });
}

function ToolbarButton({ active = false, disabled = false, label, onClick, title }: ToolbarButtonProps) {
  return (
    <Button
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      size="small"
      title={title}
      type="button"
      variant={active ? 'contained' : 'outlined'}
    >
      {label}
    </Button>
  );
}

type ToolbarGroupProps = Readonly<{
  children: React.ReactNode;
  label: string;
}>;

function ToolbarGroup({ children, label }: ToolbarGroupProps) {
  return (
    <Box sx={{ display: 'grid', gap: 0.5 }}>
      <Box sx={{ color: '#64748b', fontSize: 11, fontWeight: 700 }}>{label}</Box>
      <ButtonGroup size="small">{children}</ButtonGroup>
    </Box>
  );
}

function makeClickHandler(editor: Editor | null, command: (editor: Editor) => void) {
  return () => {
    if (editor) {
      command(editor);
    }
  };
}

export function RichTextEditor({
  disabled = false,
  label = '내용',
  minHeight = 240,
  onChange,
  value,
}: RichTextEditorProps) {
  const editor = useEditor({
    content: value || '',
    editable: !disabled,
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    immediatelyRender: false,
    onUpdate: ({ editor: currentEditor }) => {
      onChange(currentEditor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    if (editor.getHTML() !== (value || '')) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  return (
    <Box sx={{ display: 'grid', gap: 0.75 }}>
      <FormLabel sx={{ color: '#334155', fontSize: 12, fontWeight: 700 }}>{label}</FormLabel>
      <Box
        sx={{
          border: '1px solid #cbd5e1',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.25,
            p: 1,
          }}
        >
          <ToolbarGroup label="글자">
            <ToolbarButton
              active={editor?.isActive('bold')}
              disabled={!editor}
              label="굵게"
              onClick={makeClickHandler(editor, (currentEditor) =>
                currentEditor.chain().focus().toggleBold().run()
              )}
              title="굵게"
            />
            <ToolbarButton
              active={editor?.isActive('italic')}
              disabled={!editor}
              label="기울임"
              onClick={makeClickHandler(editor, (currentEditor) =>
                currentEditor.chain().focus().toggleItalic().run()
              )}
              title="기울임"
            />
            <ToolbarButton
              active={editor?.isActive('underline')}
              disabled={!editor}
              label="밑줄"
              onClick={makeClickHandler(editor, (currentEditor) =>
                currentEditor.chain().focus().toggleUnderline().run()
              )}
              title="밑줄"
            />
            <ToolbarButton
              active={editor?.isActive('strike')}
              disabled={!editor}
              label="취소선"
              onClick={makeClickHandler(editor, (currentEditor) =>
                currentEditor.chain().focus().toggleStrike().run()
              )}
              title="취소선"
            />
          </ToolbarGroup>
          <ToolbarGroup label="문단">
            <ToolbarButton
              active={editor?.isActive('paragraph')}
              disabled={!editor}
              label="본문"
              onClick={makeClickHandler(editor, (currentEditor) =>
                currentEditor.chain().focus().setParagraph().run()
              )}
              title="본문"
            />
            <ToolbarButton
              active={editor?.isActive('heading', { level: 2 })}
              disabled={!editor}
              label="큰 제목"
              onClick={makeClickHandler(editor, (currentEditor) =>
                currentEditor.chain().focus().toggleHeading({ level: 2 }).run()
              )}
              title="큰 제목"
            />
            <ToolbarButton
              active={editor?.isActive('heading', { level: 3 })}
              disabled={!editor}
              label="소제목"
              onClick={makeClickHandler(editor, (currentEditor) =>
                currentEditor.chain().focus().toggleHeading({ level: 3 }).run()
              )}
              title="작은 제목"
            />
          </ToolbarGroup>
          <ToolbarGroup label="목록">
            <ToolbarButton
              active={editor?.isActive('bulletList')}
              disabled={!editor}
              label="글머리"
              onClick={makeClickHandler(editor, (currentEditor) =>
                currentEditor.chain().focus().toggleBulletList().run()
              )}
              title="글머리 기호"
            />
            <ToolbarButton
              active={editor?.isActive('orderedList')}
              disabled={!editor}
              label="번호"
              onClick={makeClickHandler(editor, (currentEditor) =>
                currentEditor.chain().focus().toggleOrderedList().run()
              )}
              title="번호 목록"
            />
          </ToolbarGroup>
          <ToolbarGroup label="정렬">
            {(['left', 'center', 'right'] as const).map((align) => (
              <ToolbarButton
                active={editor?.isActive({ textAlign: align })}
                disabled={!editor}
                key={align}
                label={align === 'left' ? '왼쪽' : align === 'center' ? '가운데' : '오른쪽'}
                onClick={makeClickHandler(editor, (currentEditor) =>
                  currentEditor.chain().focus().setTextAlign(align).run()
                )}
                title={
                  align === 'left' ? '왼쪽 정렬' : align === 'center' ? '가운데 정렬' : '오른쪽 정렬'
                }
              />
            ))}
          </ToolbarGroup>
          <ToolbarGroup label="편집">
            <ToolbarButton
              disabled={!editor}
              label="되돌리기"
              onClick={makeClickHandler(editor, (currentEditor) =>
                currentEditor.chain().focus().undo().run()
              )}
              title="실행 취소"
            />
            <ToolbarButton
              disabled={!editor}
              label="다시 실행"
              onClick={makeClickHandler(editor, (currentEditor) =>
                currentEditor.chain().focus().redo().run()
              )}
              title="다시 실행"
            />
          </ToolbarGroup>
        </Box>
        <Box
          sx={{
            '& .ProseMirror': {
              color: '#111827',
              fontSize: 14,
              lineHeight: 1.8,
              minHeight,
              outline: 'none',
              p: 1.5,
            },
            '& .ProseMirror h2': {
              fontSize: 22,
              lineHeight: 1.45,
              margin: '0.4em 0',
            },
            '& .ProseMirror h3': {
              fontSize: 18,
              lineHeight: 1.5,
              margin: '0.4em 0',
            },
            '& .ProseMirror ol, & .ProseMirror ul': {
              paddingLeft: 3,
            },
            backgroundColor: disabled ? '#f8fafc' : '#ffffff',
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
    </Box>
  );
}

export function RichTextContent({ html, minHeight = 180 }: RichTextContentProps) {
  const cleanHtml = useMemo(() => sanitizeRichTextHtml(html), [html]);

  return (
    <Box
      dangerouslySetInnerHTML={{ __html: cleanHtml }}
      sx={{
        '& h2': {
          color: '#111827',
          fontSize: 22,
          lineHeight: 1.45,
          margin: '0.4em 0',
        },
        '& h3': {
          color: '#111827',
          fontSize: 18,
          lineHeight: 1.5,
          margin: '0.4em 0',
        },
        '& ol, & ul': {
          paddingLeft: 3,
        },
        '& p': {
          margin: '0.4em 0',
        },
        color: '#1f2937',
        fontSize: 14,
        lineHeight: 1.8,
        minHeight,
        overflowWrap: 'anywhere',
      }}
    />
  );
}
